import { useEffect, useMemo, useState } from 'react'
import { ArrowRightIcon, CheckIcon, XIcon } from '../icons'
import { Select } from '../Select'
import { formatTRY } from '../../utils/money'
import { formatGr, plateOf, totalNetGr } from '../../utils/lineMath'
import type { Tare, WeighPlate } from '../../types'
import './WeighDialog.css'

/**
 * Tartım ekranı (Tartılı ürünler modülü).
 *
 * Tabak adedi seçilir, kap seçilir (ürünün kabı hazır gelir), her tabak
 * teraziye konup okunan gram yazılır; dara düşülür, tutar kilo fiyatından.
 * Rakam tuşları dokunmatik için; bilgisayarda klavye de çalışıyor (rakamlar,
 * silme, Enter = sonraki / ekle, Esc = kapat).
 */
interface Props {
  productName: string
  perKgKurus: number
  /** Ürünün kabı; yoksa dara 0. */
  defaultTare?: Tare | null
  /** Menüde tanımlı bütün kaplar, açılır liste için. */
  tares: Tare[]
  /** Masada istenen adet (bekleyen tartım) ya da 1. */
  initialCount: number
  /** Yeniden tartımda önceki gramlar. */
  initialGross?: number[]
  /** "Sepete ekle" ya da "Tartımı kaydet". */
  confirmVerb: string
  onConfirm: (tare: Tare | undefined, plates: WeighPlate[]) => void
  onCancel: () => void
}

const keyOf = (t?: Tare | null) => (t ? `${t.name}|${t.gr}` : '')
const MAX_GR = 99999

export function WeighDialog({
  productName,
  perKgKurus,
  defaultTare,
  tares,
  initialCount,
  initialGross,
  confirmVerb,
  onConfirm,
  onCancel,
}: Props) {
  const [tareKey, setTareKey] = useState(keyOf(defaultTare))
  const [gross, setGross] = useState<(number | null)[]>(() =>
    Array.from({ length: Math.max(1, initialCount) }, (_, i) => initialGross?.[i] ?? null),
  )
  const [at, setAt] = useState(0)
  /**
   * "Sonraki"ye basılıp darayı geçmeyen tabak. Uyarı yalnızca o zaman: 580
   * yazılırken 5 ve 58 de daradan küçük, yazarken uyarı yanıp sönüyordu.
   */
  const [flagged, setFlagged] = useState<{ at: number; kind: 'empty' | 'tare' } | null>(null)

  const allTares = useMemo(() => {
    const list = [...tares]
    if (defaultTare && !list.some((t) => keyOf(t) === keyOf(defaultTare))) list.unshift(defaultTare)
    return list
  }, [tares, defaultTare])
  const tare = allTares.find((t) => keyOf(t) === tareKey)
  const tareGr = tare?.gr ?? 0

  // Tabak sayısı listenin uzunluğu. Ayrı bir "adet" düğmesi yok: numaralar
  // yalnızca o tabağa geçirir, tabak yalnızca × ile silinir. (Adet düğmeleri
  // "o tabağa git" sanılıyor, basınca girilen gramlar kayboluyordu.)
  const count = gross.length
  const shown = gross
  const plates = shown.map((g) => (g === null ? null : plateOf(g, tareGr, perKgKurus)))
  const okPlate = (g: number | null) => g !== null && g > tareGr
  const ready = shown.every(okPlate)
  const done = plates.filter((p): p is WeighPlate => p !== null && p.netGr > 0)
  const totalKurus = done.reduce((s, p) => s + p.kurus, 0)

  const addPlate = () => {
    if (count >= 50) return
    setGross((g) => [...g, null])
    setAt(count)
  }

  const removePlate = (i: number) => {
    if (count <= 1) return
    setGross((g) => g.filter((_, k) => k !== i))
    setAt((a) => (a > i ? a - 1 : Math.min(a, count - 2)))
  }

  const typeDigit = (d: string) => {
    setFlagged(null)
    setGross((g) => {
      const cur = g[at] == null ? '' : String(g[at])
      const nextStr = (cur + d).replace(/^0+(?=\d)/, '')
      const v = Number(nextStr)
      if (v > MAX_GR) return g
      const copy = [...g]
      copy[at] = v
      return copy
    })
  }

  const backspace = () => {
    setFlagged(null)
    setGross((g) => {
      const cur = g[at] == null ? '' : String(g[at])
      const s = cur.slice(0, -1)
      const copy = [...g]
      copy[at] = s === '' ? null : Number(s)
      return copy
    })
  }

  const clear = () =>
    setGross((g) => {
      const copy = [...g]
      copy[at] = null
      return copy
    })

  const next = () => {
    if (!okPlate(gross[at] ?? null)) {
      if (gross[at] != null) setFlagged({ at, kind: 'tare' })
      return
    }
    setFlagged(null)
    if (at < count - 1) setAt(at + 1)
    else if (ready) confirm()
    else showProblem()
  }

  /** Hazır değilken: sorunlu ilk tabağa geç ve nedenini yaz. */
  const showProblem = () => {
    const miss = shown.findIndex((g) => !okPlate(g))
    if (miss < 0) return
    setAt(miss)
    setFlagged({ at: miss, kind: shown[miss] == null ? 'empty' : 'tare' })
  }

  const confirm = () => {
    if (!ready) {
      showProblem()
      return
    }
    onConfirm(tare ? { name: tare.name, gr: tare.gr } : undefined, plates as WeighPlate[])
  }

  // Bilgisayar klavyesi: terazinin yanındaki kasada elle yazmak daha hızlı.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement
      if (el && (el.tagName === 'SELECT' || el.tagName === 'INPUT')) return
      if (/^\d$/.test(e.key)) typeDigit(e.key)
      else if (e.key === 'Backspace') backspace()
      else if (e.key === 'Enter') next()
      else if (e.key === 'Escape') onCancel()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const cur = gross[at] ?? null
  const curWarn = flagged?.at === at && !okPlate(cur)

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-label={`${productName} tartımı`}>
      <div className="weigh">
        <header className="weigh__head">
          <h2>{productName}</h2>
          <button type="button" className="weigh__x" onClick={onCancel} aria-label="Kapat">
            <XIcon />
          </button>
        </header>

        <div className="weigh__body">
          <div className="weigh__left">
            <div className="weigh__step">
              <span className="weigh__label">Porsiyonlar ({count})</span>
              <div className="weigh__nav" role="tablist" aria-label="Porsiyon seç">
                {shown.map((g, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === at}
                    className={`${i === at ? 'is-on' : ''} ${okPlate(g) ? 'is-done' : ''}`}
                    onClick={() => setAt(i)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button type="button" className="weigh__add" onClick={addPlate}>
                  + Porsiyon ekle
                </button>
              </div>
            </div>

            <div className="weigh__step">
              <span className="weigh__label" id="weigh-tare-label">Kap</span>
              <Select
                value={tareKey}
                onChange={setTareKey}
                aria-labelledby="weigh-tare-label"
                options={[
                  { value: '', label: 'Kap yok', hint: 'dara 0 g' },
                  ...allTares.map((t) => ({ value: keyOf(t), label: t.name, hint: `−${t.gr} g` })),
                ]}
              />
            </div>

            <div className="weigh__step">
              <span className="weigh__label">
                Şu an tartılan: <b>Porsiyon {at + 1}</b>
                {count > 1 && <> / {count}</>}
              </span>
              {/* Uyarı göstergenin içinde, solda: ayrı bir satır ya da boşluk
                  yok, tuş takımı göstergenin hemen altında kalıyor. */}
              <div className={`weigh__display ${curWarn ? 'is-warn' : ''}`} aria-live="polite">
                {curWarn ? (
                  <span className="weigh__msg">
                    {cur === null ? `Porsiyon ${at + 1} tartılmadı` : `Darayı (${tareGr} g) geçmiyor`}
                  </span>
                ) : (
                  cur === null && <span className="weigh__ph">Terazideki gram</span>
                )}
                {cur !== null && (
                  <span className="weigh__num">
                    {cur} <small>g</small>
                  </span>
                )}
              </div>
            </div>

            <div className="weigh__pad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} type="button" onClick={() => typeDigit(d)}>
                  {d}
                </button>
              ))}
              <button type="button" className="weigh__pad-c" onClick={clear}>
                C
              </button>
              <button type="button" onClick={() => typeDigit('0')}>
                0
              </button>
              <button type="button" className="weigh__pad-bs" onClick={backspace} aria-label="Son rakamı sil">
                ⌫
              </button>
              <button
                type="button"
                className="weigh__pad-next"
                onClick={next}
                disabled={cur === null}
              >
                {at < count - 1 ? (
                  <>
                    Sonraki <ArrowRightIcon />
                  </>
                ) : (
                  <>
                    Tamam <CheckIcon />
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="weigh__right">
            <div className="weigh__sumhead">
              <span className="weigh__label">Hesap özeti</span>
              <span className="weigh__rate">{formatTRY(perKgKurus)} / kg</span>
            </div>
            <ol className="weigh__plates">
              {shown.map((g, i) => {
                const p = plates[i]
                return (
                  <li key={i} className="weigh__row">
                    <button
                      type="button"
                      className={`weigh__pbtn ${i === at ? 'is-on' : ''} ${g !== null && !okPlate(g) ? 'is-bad' : ''}`}
                      onClick={() => setAt(i)}
                    >
                      <span className="weigh__pn">Porsiyon {i + 1}</span>
                      {g === null || !p ? (
                        <span className="weigh__pw weigh__pw--empty">tartılmadı</span>
                      ) : (
                        <>
                          <span className="weigh__pw">
                            {g} g → <b>{p.netGr} g</b>
                          </span>
                          <span className="weigh__pk">{formatTRY(p.kurus)}</span>
                        </>
                      )}
                    </button>
                    {count > 1 && (
                      <button
                        type="button"
                        className="weigh__rm"
                        onClick={() => removePlate(i)}
                        aria-label={`Porsiyon ${i + 1}'i sil`}
                        title="Porsiyonu sil"
                      >
                        ×
                      </button>
                    )}
                  </li>
                )
              })}
            </ol>

            <div className="weigh__total">
              <div>
                <span>Toplam net</span>
                <b>{formatGr(totalNetGr(done))}</b>
              </div>
              <div className="weigh__sum">
                <span>Toplam tutar</span>
                <strong>{formatTRY(totalKurus)}</strong>
              </div>
            </div>

            {/* Hep basılabilir: hazır değilken sorunlu tabağa götürüp nedenini söylüyor. */}
            <button
              type="button"
              className={`weigh__confirm ${ready ? '' : 'is-waiting'}`}
              onClick={confirm}
            >
              {count} porsiyonu {confirmVerb}
              <small>{formatTRY(totalKurus)}</small>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

